import { z } from 'zod';
import { normalizeToMidnight } from '#client/utils/helpers';
import { SHARED_CONSTANTS } from '#shared/sharedConstants';

// Base schema without the refine
export const baseClientProgramSchema = z
    .object({
        name: z.string().min(1, 'Program name is required'),
        companyId: z.number().positive('Company is required'),
        programAdmins: z
            .array(z.string().email('Invalid email format'))
            .min(1, 'At least one program admin is required')
            .max(25, 'Maximum 25 program admins are allowed')
            .refine(
                (emails) => {
                    const uniqueEmails = new Set(emails.map((email) => email.toLowerCase()));
                    return uniqueEmails.size === emails.length;
                },
                {
                    message: 'Duplicate emails are not allowed',
                }
            ),
        restrictToEligibleProducts: z.boolean().default(true),
        eligibleProductRules: z.string().trim().default(''),
        programDescription: z.string().min(1, 'Program description is required'),
        isInStoreOnly: z.boolean().default(true),
        conditionType: z.enum(SHARED_CONSTANTS.PROGRAM_CONDITION_TYPES, {
            errorMap: () => ({ message: 'Condition type must be "All Conditions", "Any Conditions" or "Grouped rules"' }),
        }),
        dateStart: z.date().refine(
            (date) => {
                const today = normalizeToMidnight(new Date());
                const startDate = normalizeToMidnight(date);
                return startDate >= today;
            },
            {
                message: 'Start date must be today or a future date',
            }
        ),
        dateExpires: z.date().refine(
            (date) => {
                const today = normalizeToMidnight(new Date());
                const expirationDate = normalizeToMidnight(date);
                return expirationDate >= today;
            },
            {
                message: 'Expiration date must be today or a future date',
            }
        ),
        discountType: z.union([z.literal(''), z.enum(['none', 'percentage', 'fixed'])]),
        subsidyType: z.union([z.literal(''), z.enum(['total-value', 'number-of-items', 'percentage-subsidy'])]),
        discountAmount: z.number(),
        maxItems: z.number(),
        maxValue: z.number(),
        // New snapshot-based fields
        productSnapshotId: z.string().nullable().optional(),
        programProductOverrides: z
            .array(
                z.object({
                    productId: z.string().min(1, 'Product ID is required'),
                    variantId: z.string().optional(),
                    exclude: z.boolean().default(false),
                    priceOverridden: z.boolean().default(false),
                    programPrice: z.number().nullable().optional(),
                })
            )
            .nullable()
            .optional(),
        // Legacy field for backward compatibility
        programProducts: z
            .array(
                z.object({
                    id: z.string(),
                })
            )
            .optional(),
    })
    .superRefine((data, ctx) => {
        // eligibleProductRules required only in filter mode; grouped rules are sent as ruleGroups and checked by the server
        if (data.restrictToEligibleProducts && data.conditionType !== 'groupedRules' && (!data.eligibleProductRules || data.eligibleProductRules.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Eligible product rules are required in filter mode',
                path: ['eligibleProductRules'],
            });
        }
        // Discount amount is required when discount type is not None
        if (data.discountType && data.discountType !== 'none' && (!data.discountAmount || data.discountAmount <= 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Discount amount is required when discount type is not None',
                path: ['discountAmount'],
            });
        }

        // Subsidy value is required when subsidy type is selected
        if (data.subsidyType && data.subsidyType !== '' && (!data.maxValue || data.maxValue <= 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Subsidy value is required when subsidy type is selected',
                path: ['maxValue'],
            });
        }
    });

// Partial schema for updates (without superRefine to avoid ZodEffects)
export const partialProgramSchema = z.object({
    name: z.string().min(1, 'Program name is required').optional(),
    companyId: z.number().positive('Company is required').optional(),
    programAdmins: z
        .array(z.string().email('Invalid email format'))
        .min(1, 'At least one program admin is required')
        .max(25, 'Maximum 25 program admins are allowed')
        .refine(
            (emails) => {
                const uniqueEmails = new Set(emails.map((email) => email.toLowerCase()));
                return uniqueEmails.size === emails.length;
            },
            {
                message: 'Duplicate emails are not allowed',
            }
        )
        .optional(),
    eligibleProductRules: z.string().min(1, 'Eligible product rules are required').optional(),
    programDescription: z.string().min(1, 'Program description is required').optional(),
    isInStoreOnly: z.boolean().optional(),
    conditionType: z.enum(SHARED_CONSTANTS.PROGRAM_CONDITION_TYPES).optional(),
    dateStart: z
        .date()
        .refine(
            (date) => {
                const today = normalizeToMidnight(new Date());
                const startDate = normalizeToMidnight(date);
                return startDate >= today;
            },
            {
                message: 'Start date must be today or a future date',
            }
        )
        .optional(),
    dateExpires: z
        .date()
        .refine(
            (date) => {
                const today = normalizeToMidnight(new Date());
                const expirationDate = normalizeToMidnight(date);
                return expirationDate >= today;
            },
            {
                message: 'Expiration date must be today or a future date',
            }
        )
        .optional(),
    discountType: z.union([z.literal(''), z.enum(['none', 'percentage', 'fixed'])]).optional(),
    subsidyType: z.union([z.literal(''), z.enum(['total-value', 'number-of-items', 'percentage-subsidy'])]).optional(),
    discountAmount: z.number().optional(),
    maxItems: z.number().optional(),
    maxValue: z.number().optional(),
    // New snapshot-based fields
    productSnapshotId: z.string().nullable().optional(),
    programProductOverrides: z
        .array(
            z.object({
                productId: z.string().min(1, 'Product ID is required'),
                variantId: z.string().optional(),
                exclude: z.boolean().default(false),
                priceOverridden: z.boolean().default(false),
                programPrice: z.number().nullable().optional(),
            })
        )
        .nullable()
        .optional(),
    // Legacy field for backward compatibility
    products: z
        .array(
            z.object({
                sku: z.string().optional(),
                productId: z.string().min(1, 'Product ID is required'),
                productName: z.string().min(1, 'Product name is required'),
            })
        )
        .optional(),
});

// Full schema with the date validation refine
export const clientProgramSchema = baseClientProgramSchema.refine(
    (data) => {
        const startDate = normalizeToMidnight(data.dateStart);
        const expiresDate = normalizeToMidnight(data.dateExpires);
        return expiresDate > startDate;
    },
    {
        message: 'Expiration date must be after start date',
        path: ['dateExpires'],
    }
);

// Minimal Zod schema for just eligibleProductRules
export const eligibleProductRulesSchema = z.object({
    eligibleProductRules: z.string().min(1, 'Product filter rules are required'),
});

export const validatePartialProgramSchema = (data) => {
    // Ensure programAdmins is an array of strings
    const transformedData = { ...data };
    if (transformedData.programAdmins && Array.isArray(transformedData.programAdmins)) {
        transformedData.programAdmins = transformedData.programAdmins.map((admin) => (typeof admin === 'string' ? admin : admin.email)).filter((email) => email && email.trim() !== '');
    }

    try {
        const result = partialProgramSchema.safeParse(transformedData);

        if (!result.success) {
            const formattedErrors = result.error.format();
            const flattenedErrors = flattenFormattedErrors(formattedErrors);

            return {
                success: false,
                errors: flattenedErrors,
            };
        }

        return { success: true, errors: {} };
    } catch (error) {
        console.error('Error in validatePartialProgramSchema:', error);
        throw error;
    }
};

export const validateWholeProgramSchema = (data) => {
    // Ensure programAdmins is an array of strings
    const transformedData = { ...data };
    if (transformedData.programAdmins && Array.isArray(transformedData.programAdmins)) {
        transformedData.programAdmins = transformedData.programAdmins.map((admin) => (typeof admin === 'string' ? admin : admin.email)).filter((email) => email && email.trim() !== '');
    }

    const result = clientProgramSchema.safeParse(transformedData);

    if (!result.success) {
        const formattedErrors = result.error.format();
        const flattenedErrors = flattenFormattedErrors(formattedErrors);

        return {
            success: false,
            errors: flattenedErrors,
        };
    } else {
        return { success: true, errors: {} };
    }
};

/**
 * Validates only eligibleProductRules and flattens errors.
 * @param {object} data - Object with eligibleProductRules property
 * @returns {object} { success: boolean, errors: object }
 */
export const validateEligibleProductRules = (data) => {
    const result = eligibleProductRulesSchema.safeParse({
        eligibleProductRules: data.eligibleProductRules,
    });

    if (!result.success) {
        const formattedErrors = result.error.format();
        const flattenedErrors = flattenFormattedErrors(formattedErrors);
        return {
            success: false,
            errors: flattenedErrors,
        };
    }
    return { success: true, errors: {} };
};

/**
 * Flattens Zod's .format() tree into a flat error map using dot notation.
 * @param formattedError - The result of `zodError.format()`
 * @param prefix - Internal use for recursion
 * @returns Record<string, string[]> of field paths to error messages
 */
export const flattenFormattedErrors = (formattedError, prefix = '') => {
    const errorMap = {};

    for (const key in formattedError) {
        if (key === '_errors') {
            if (formattedError[key].length > 0 && prefix) {
                errorMap[prefix] = formattedError[key];
            } else if (formattedError[key].length > 0 && !prefix) {
                // Handle root-level errors (like refine errors)
                errorMap['_root'] = formattedError[key];
            }
        } else if (Array.isArray(formattedError[key])) {
            formattedError[key].forEach((item, index) => {
                const newPrefix = prefix ? `${prefix}.${key}[${index}]` : `${key}[${index}]`;
                const childErrors = flattenFormattedErrors(item, newPrefix);
                Object.assign(errorMap, childErrors);
            });
        } else if (typeof formattedError[key] === 'object' && formattedError[key] !== null) {
            const newPrefix = prefix ? `${prefix}.${key}` : key;

            // Capture field-specific custom errors
            if (formattedError[key]._errors && formattedError[key]._errors.length > 0) {
                errorMap[newPrefix] = formattedError[key]._errors;
            }

            const childErrors = flattenFormattedErrors(formattedError[key], newPrefix);
            Object.assign(errorMap, childErrors);
        }
    }

    return errorMap;
};
